import { useState, useEffect } from 'react';
import { Container, Typography, Paper, Select, MenuItem, FormControl, InputLabel, Box, CircularProgress, Alert, IconButton, Tooltip } from '@mui/material';
import { DataGrid } from '@mui/x-data-grid';
import type { GridColDef } from '@mui/x-data-grid';
import { supabase } from '../lib/supabaseClient';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import DeleteIcon from '@mui/icons-material/Delete';

interface Report {
  report_id: string;
  created_at: string;
  username: string;
}

interface ReportRecord {
  id: number;
  activity_name: string;
  remarks: string;
  ideal_time: string;
  actual_start_time: string;
  actual_end_time: string;
}

const columns: GridColDef[] = [
  { field: 'activity_name', headerName: 'Activity', width: 400, cellClassName: 'wrap-text' },
  { 
    field: 'ideal_time', 
    headerName: 'Ideal Time', 
    width: 150,
    valueFormatter: (value) => value ? dayjs(value as string).format('HH:mm:ss') : 'N/A',
  },
  { 
    field: 'actual_start_time', 
    headerName: 'HOTO Start', 
    width: 150,
    valueFormatter: (value) => value ? dayjs(value as string).format('HH:mm:ss') : 'N/A',
  },
  { 
    field: 'actual_end_time', 
    headerName: 'HOTO End', 
    width: 150,
    valueFormatter: (value) => value ? dayjs(value as string).format('HH:mm:ss') : 'N/A',
  },
  { field: 'remarks', headerName: 'Remarks', width: 400, cellClassName: 'wrap-text' },
];

const HOTOTimingsReport = () => {
  const { user } = useAuth();
  const [userRole, setUserRole] = useState('');
  const [reports, setReports] = useState<Report[]>([]);
  const [selectedReportId, setSelectedReportId] = useState<string>('');
  const [reportData, setReportData] = useState<ReportRecord[]>([]);
  const [loadingReports, setLoadingReports] = useState(true);
  const [loadingData, setLoadingData] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchUserRole = async () => {
      if (user) {
        const { data } = await supabase
          .from('users')
          .select('user_role')
          .eq('id', user.id)
          .single();
        if (data) setUserRole(data.user_role);
      }
    };
    fetchUserRole();
  }, [user]);

  useEffect(() => {
    const fetchReports = async () => {
      setLoadingReports(true);
      try {
        const { data, error } = await supabase
          .from('hoto_records')
          .select('report_id, created_at, saved_by, users ( username )')
          .order('created_at', { ascending: false });

        if (error) throw error;
        
        const uniqueReports: Report[] = [];
        const seen = new Set();
        data.forEach((item: any) => {
          if (!seen.has(item.report_id)) {
            seen.add(item.report_id);
            uniqueReports.push({
              report_id: item.report_id,
              created_at: item.created_at,
              username: item.users?.username || 'Unknown User',
            });
          }
        });

        setReports(uniqueReports);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch reports.');
      } finally {
        setLoadingReports(false);
      }
    };

    fetchReports();
  }, []);

  useEffect(() => {
    if (!selectedReportId) return;

    const fetchReportData = async () => {
      setLoadingData(true);
      setReportData([]);
      try {
        const { data, error } = await supabase
          .from('hoto_records')
          .select('*')
          .eq('report_id', selectedReportId)
          .order('ideal_time', { ascending: true });

        if (error) throw error;
        setReportData(data || []);

      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch report data.');
      } finally {
        setLoadingData(false);
      }
    };

    fetchReportData();
  }, [selectedReportId]);

  const handleDeleteReport = async () => {
    if (!selectedReportId) return;

    const confirmed = window.confirm('Are you sure you want to delete this entire report? This action cannot be undone.');
    if (!confirmed) return;

    try {
      const { error } = await supabase.from('hoto_records').delete().eq('report_id', selectedReportId);
      if (error) throw error;
      
      setSelectedReportId('');
      setReportData([]);
      const newReports = reports.filter(r => r.report_id !== selectedReportId);
      setReports(newReports);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete report.');
    }
  };

  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 4 }}>
      <Paper sx={{ p: 3, '.wrap-text': { whiteSpace: 'normal', lineHeight: 'auto', maxHeight: 'none !important' } }}>
        <Typography variant="h4" component="h1" gutterBottom sx={{ fontWeight: 'bold' }}>
          HOTO Timings Report
        </Typography>
        
        {loadingReports ? (
          <CircularProgress />
        ) : error ? (
           <Alert severity="error">{error}</Alert>
        ) : (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <FormControl fullWidth sx={{ my: 2 }}>
              <InputLabel id="report-select-label">Select a Report</InputLabel>
              <Select
                labelId="report-select-label"
                value={selectedReportId}
                label="Select a Report"
                onChange={(e) => setSelectedReportId(e.target.value as string)}
              >
                <MenuItem value=""><em>None</em></MenuItem>
                {reports.map((report) => (
                  <MenuItem key={report.report_id} value={report.report_id}>
                    {`Report from ${dayjs(report.created_at).format('DD MMM YYYY, HH:mm')} (saved by ${report.username})`}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {(userRole === 'admin' || userRole === 'god') && selectedReportId && (
              <Tooltip title="Delete Report">
                <IconButton onClick={handleDeleteReport} color="error">
                  <DeleteIcon />
                </IconButton>
              </Tooltip>
            )}
          </Box>
        )}

        {loadingData ? (
           <Box sx={{ display: 'flex', justifyContent: 'center', my: 4 }}><CircularProgress /></Box>
        ) : selectedReportId && reportData.length > 0 ? (
          <Box sx={{ height: 'auto', width: '100%', mt: 2 }}>
            <DataGrid
              rows={reportData}
              columns={columns}
              getRowHeight={() => 'auto'}
              initialState={{
                pagination: {
                  paginationModel: { pageSize: 100, page: 0 },
                },
              }}
              pageSizeOptions={[10, 25, 50, 100]}
              checkboxSelection={false}
              disableRowSelectionOnClick
            />
          </Box>
        ) : selectedReportId && (
            <Alert severity="info" sx={{ mt: 2 }}>No data found for this report.</Alert>
        )}
      </Paper>
    </Container>
  );
};

export default HOTOTimingsReport;